/**
 * 瀏覽器推送通知管理器
 * 使用 Web Notification API 實現即時推送
 */

class NotificationManager {
  constructor() {
    // 安全檢查 Notification API 是否可用
    this.permission = this.isSupported() ? Notification.permission : 'denied';
    this.pollingInterval = null;
    this.lastNotificationTime = Date.now();
  }

  /**
   * 檢查瀏覽器是否支持推送通知
   */
  isSupported() {
    return typeof window !== 'undefined' && 'Notification' in window;
  }

  /**
   * 請求推送通知權限
   */
  async requestPermission() {
    if (!this.isSupported()) {
      console.warn('此瀏覽器不支持推送通知');
      return false;
    }

    if (this.permission === 'granted') {
      return true;
    }

    try {
      const permission = await Notification.requestPermission();
      this.permission = permission;
      return permission === 'granted';
    } catch (error) {
      console.error('請求通知權限失敗:', error);
      return false;
    }
  }

  /**
   * 顯示推送通知
   */
  showNotification(title, options = {}) {
    if (!this.isSupported()) {
      console.warn('瀏覽器不支持推送通知');
      return null;
    }

    if (this.permission !== 'granted') {
      console.warn('沒有推送通知權限');
      return null;
    }

    try {
      const notification = new Notification(title, {
        icon: '/logo192.png', // 使用應用 logo
        badge: '/logo192.png',
        vibrate: [200, 100, 200], // 震動模式
        requireInteraction: false, // 自動關閉
        ...options
      });

      // 點擊通知時跳轉到通知收件夾
      notification.onclick = () => {
        window.focus();
        window.location.href = '/#/student/notifications';
        notification.close();
      };

      return notification;
    } catch (error) {
      console.error('顯示通知失敗:', error);
      return null;
    }
  }

  /**
   * 開始輪詢新通知
   */
  startPolling(apiUrl, token, onNewNotification) {
    // 停止之前的輪詢
    this.stopPolling();

    console.log('🔔 推送通知輪詢已啟動');
    console.log(`權限狀態: ${this.permission}`);
    console.log(`最後通知時間: ${new Date(this.lastNotificationTime).toLocaleString('zh-TW')}`);

    // 每5秒檢查一次新通知（更即時）
    this.pollingInterval = setInterval(async () => {
      try {
        const response = await fetch(`${apiUrl}/notifications`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const notifications = await response.json();
          
          console.log(`📨 檢查通知: 共 ${notifications.length} 則`);
          
          // 檢查是否有新通知（未讀且創建時間在上次檢查之後）
          const newNotifications = notifications.filter(notif => {
            const createdAt = new Date(notif.created_at).getTime();
            return !notif.is_read && createdAt > this.lastNotificationTime;
          });

          console.log(`🆕 新通知數量: ${newNotifications.length}`);

          if (newNotifications.length > 0) {
            console.log('🔔 準備顯示推送通知...');
            
            // 顯示所有新通知（最多3條）
            const toShow = newNotifications.slice(0, 3);
            toShow.forEach((notif, index) => {
              setTimeout(() => {
                const result = this.showNotification(notif.title, {
                  body: notif.message,
                  tag: notif.notification_id, // 避免重複顯示
                  requireInteraction: true // 需要用戶交互才關閉（更明顯）
                });
                
                if (result) {
                  console.log(`✅ 通知已顯示: ${notif.title}`);
                } else {
                  console.warn(`❌ 通知顯示失敗: ${notif.title}`);
                }
              }, index * 500); // 每條通知間隔500ms
            });

            // 更新最後通知時間為最新的通知
            const latestTime = Math.max(...newNotifications.map(n => new Date(n.created_at).getTime()));
            this.lastNotificationTime = latestTime;
            console.log(`更新最後通知時間: ${new Date(latestTime).toLocaleString('zh-TW')}`);

            // 回調函數
            if (onNewNotification) {
              onNewNotification(newNotifications);
            }
          }
        } else {
          console.error('獲取通知失敗:', response.status);
        }
      } catch (error) {
        console.error('輪詢通知失敗:', error);
      }
    }, 5000); // 每5秒檢查一次（更即時）

    console.log('✅ 推送通知輪詢已啟動 (每5秒檢查一次)');
  }

  /**
   * 停止輪詢
   */
  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
      console.log('推送通知輪詢已停止');
    }
  }

  /**
   * 獲取當前權限狀態
   */
  getPermission() {
    return this.permission;
  }
}

// 創建單例
const notificationManager = new NotificationManager();

export default notificationManager;
